import React, { useState, useRef } from "react";
import { useSelector } from "react-redux";
import {
  ChevronDown,
  Menu,
  X,
  Bell,
  Moon,
  Sun,
  User,
  LogOut,
  Home,
  Settings,
  Layers,
  Frown,
  Edit,
  Users,
  Info,
  FileArchive,
  Car,
  Shield,
} from "lucide-react";
import logo from "../../assets/logotype.jpg";
import user_pic from "../../assets/user.jpg";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import "./Navbar.css";

const Navbar = ({ darkMode, setDarkMode }) => {
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const { logout } = useAuth();
  const navigate = useNavigate();
  const dropdownTimerRef = useRef(null);

  const menuItems = [
    {
      path: "/",
      label: "Accueil",
      icon: Home,
      hasSubmenu: false,
    },
    {
      label: "Production",
      path: "/products",
      icon: Edit,
      hasSubmenu: true,
      submenu: [
        { label: "Contrats Vie", icon: FileArchive, path: "/products/life" },
        { label: "Contrats Iard", icon: Car, path: "/products/nonlife" },
        { label: "Mes engins", icon: Car, path: "/products/home-garden" },
        {
          label: "Les Attestations",
          icon: FileArchive,
          path: "/products/attestation",
        },
      ],
    },
    {
      label: "Sinistres",
      icon: Frown,
      path: "/services",
      hasSubmenu: true,
      submenu: [
        { label: "Mes Sinistres", icon: User, path: "/services/delivery" },
        {
          label: "Mes Déclarations",
          icon: User,
          path: "/services/installation",
        },
        { label: "Mes Plaintes", icon: User, path: "/services/support" },
      ],
    },
    {
      label: "Prestations",
      path: "/prestations",
      icon: Layers,
      hasSubmenu: true,
      submenu: [
        { label: "Vie", icon: User, path: "/prestations/life" },
        { label: "Non-Vie", icon: User, path: "/prestations/nonlife" },
        { label: "Contact", icon: User, path: "/prestations/contact" },
      ],
    },
    {
      label: "Actualités",
      path: "/actualites",
      icon: Info,
      hasSubmenu: true,
      submenu: [
        { label: "Blog", icon: User, path: "/actualites/blog" },
        { label: "FAQs", icon: User, path: "/actualites/faqs" },
        { label: "Retours", icon: User, path: "/actualites/returns" },
      ],
    },
    {
      label: "Administration",
      path: "/admin",
      icon: Settings,
      hasSubmenu: true,
      submenu: [
        { label: "Utilisateurs", icon: Users, path: "/admin/users" },
        { label: "Entités", icon: Layers, path: "/admin/entities" },
        { label: "Rôles", icon: Shield, path: "/admin/roles" },
        { label: "Permissions", icon: Settings, path: "/admin/permissions" },
        { label: "Paramètres", icon: Settings, path: "/admin/settings" },
      ],
    },
  ];

  const handleDropdownEnter = (index) => {
    if (dropdownTimerRef.current) {
      clearTimeout(dropdownTimerRef.current);
      dropdownTimerRef.current = null;
    }
    setActiveDropdown(index);
  };

  const handleDropdownLeave = () => {
    dropdownTimerRef.current = setTimeout(() => {
      setActiveDropdown(null);
    }, 300);
  };

  const handleProfileToggle = () => {
    setIsProfileOpen(!isProfileOpen);
  };

  const handleNavigation = (path) => {
    navigate(path);
    setActiveDropdown(null);
    setIsProfileOpen(false);
  };

  const handleLogout = () => {
    logout();
    navigate("/");
    setIsProfileOpen(false);
  };

  return (
    <div className={`compact-navbar ${darkMode ? "dark" : ""}`}>
      {/* Left Side - Logo & Menu Items */}
      <div className="nav-left">
        <div className="logo">
          <img src={logo} alt="Logo" width={88} height={48} />
        </div>
        {/* Desktop Menu */}
        <div className="desktop-menu">
          {menuItems.map((item, index) => (
            <React.Fragment key={index}>
              {index > 0 && <div className="menu-separator"></div>}
              <div
                className="menu-item"
                onMouseEnter={() => handleDropdownEnter(index)}
                onMouseLeave={handleDropdownLeave}
              >
                <span
                  className="menu-link"
                  onClick={() => handleNavigation(item.path)}
                >
                  <item.icon size={18} strokeWidth={2} />
                  <span>{item.label}</span>
                  {item.submenu && (
                    <ChevronDown
                      size={16}
                      strokeWidth={2}
                      className={`arrow ${
                        activeDropdown === index ? "rotate" : ""
                      }`}
                    />
                  )}
                </span>

                {item.submenu && activeDropdown === index && (
                  <div className="dropdown-menu">
                    {item.submenu.map((sub, i) => (
                      <div
                        key={i}
                        className="dropdown-item"
                        onClick={() => handleNavigation(sub.path)}
                      >
                        <sub.icon size={16} strokeWidth={2} /> {sub.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Right Side - Icons */}
      <div className="nav-right">
        <button className="theme-btn" onClick={() => setDarkMode(!darkMode)}>
          {darkMode ? (
            <Sun size={18} strokeWidth={2} />
          ) : (
            <Moon size={18} strokeWidth={2} />
          )}
        </button>
        <div className="notifications">
          <Bell size={18} strokeWidth={2} />
          <span className="badge">3</span>
        </div>
        <div className="user-menu-container">
          <div
            className="user-avatar"
            onClick={(e) => {
              e.preventDefault();
              setUserDropdownOpen(!userDropdownOpen);
            }}
          >
            <img src={user_pic} alt="User" width={36} height={36} />
          </div>
          {userDropdownOpen && (
            <div className={`user-dropdown ${darkMode ? "dark" : ""}`}>
              <button
                className="dropdown-item"
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/admin/profile");
                }}
              >
                <User size={16} strokeWidth={2} /> Profil
              </button>
              <button
                className="dropdown-item"
                onClick={(e) => {
                  e.preventDefault();
                  handleLogout();
                }}
              >
                <LogOut size={16} strokeWidth={2} /> Déconnexion
              </button>
            </div>
          )}
        </div>

        {/* Mobile Menu Button */}
        <button
          className="mobile-btn"
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          {mobileOpen ? (
            <X size={20} strokeWidth={2} />
          ) : (
            <Menu size={20} strokeWidth={2} />
          )}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="mobile-menu">
          {menuItems.map((item, index) => (
            <React.Fragment key={index}>
              <div className="mobile-menu-item">
                <span
                  className="mobile-link"
                  onClick={() => handleNavigation(item.path)}
                >
                  <item.icon size={18} strokeWidth={2} />
                  <span>{item.label}</span>
                  {item.submenu && (
                    <ChevronDown
                      size={16}
                      strokeWidth={2}
                      className={`arrow ${
                        activeDropdown === index ? "rotate" : ""
                      }`}
                    />
                  )}
                </span>

                {item.submenu && activeDropdown === index && (
                  <div className="mobile-dropdown">
                    {item.submenu.map((sub, i) => (
                      <div
                        key={i}
                        className="mobile-dropdown-item"
                        onClick={() => handleNavigation(sub.path)}
                      >
                        <sub.icon size={16} strokeWidth={2} /> {sub.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
};

export default Navbar;
